/**
 * useClientContact — telefone e e-mail do cliente vinculado a um documento.
 *
 * Existe porque os documentos guardam SNAPSHOTS parciais do cliente e cada um
 * guarda um pedaço diferente:
 *   * `contracts` tem `client_phone`, mas NÃO tem e-mail;
 *   * `service_orders` tem `client_phone`;
 *   * `receipts` só tem `payer_name`/`payer_document` — nenhum contato.
 * Sem isto, "enviar por WhatsApp" no recibo abriria o seletor de contato vazio e
 * "enviar por e-mail" no contrato abriria o mailto sem destinatário.
 *
 * Leitura offline-first pelo cache de clientes (aquecido pelo
 * OfflineDataCacheWarmup), então compor a mensagem continua funcionando sem rede.
 * O snapshot do documento tem PRECEDÊNCIA sobre o cadastro quando existe — ele é
 * o dado do momento em que o documento foi emitido.
 */
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/useCompany';
import { supabase } from '@/integrations/supabase/client';
import { getCachedClients } from '@/lib/offlineDataCache';

export interface ClientContact {
  phone: string | null;
  email: string | null;
}

const EMPTY: ClientContact = { phone: null, email: null };

export function useClientContact(clientId?: string | null): ClientContact {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const companyId = activeCompany?.id ?? null;

  const { data: clients = [] } = useQuery({
    queryKey: ['client-contacts', companyId],
    enabled: Boolean(user?.id && companyId && clientId),
    networkMode: 'offlineFirst',
    staleTime: 5 * 60 * 1000,
    // A query nasce DESABILITADA (sessão/empresa resolvem depois da montagem), então
    // `refetchOnMount` já passou quando ela habilita: sem o carimbo, o cache semeado
    // conta como FRESCO e a primeira busca nunca sai (#158).
    initialDataUpdatedAt: 0,
    initialData: () => (user?.id ? getCachedClients(user.id) ?? undefined : undefined),
    queryFn: async () => {
      if (!user?.id || !companyId) return [];
      const { data, error } = await supabase
        .from('clients')
        .select('id, phone, email')
        .eq('company_id', companyId);
      if (error) throw error;
      return data ?? [];
    },
  });

  if (!clientId) return EMPTY;
  const match = (clients as { id: string; phone?: string | null; email?: string | null }[]).find(
    (c) => c.id === clientId,
  );
  return { phone: match?.phone ?? null, email: match?.email ?? null };
}
